import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { PermitLetter, LetterGeneration, Employee } from '../../types';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Input from '../Form/Input';
import Button from '../Form/Button';
import { useBranches } from '../../hooks/useBranches';
import { generatePersonnelPreviewHTML, buildPersonnelPrintHTML } from './PersonnelLetterTemplate';
import LetterPreviewPanel from './LetterPreviewPanel';

interface PersonnelLetterGeneratorProps {
  letters: PermitLetter[];
  employees: Employee[];
  onSubmit: (data: Partial<LetterGeneration>) => void;
  onCancel: () => void;
}

const PersonnelLetterGenerator: React.FC<PersonnelLetterGeneratorProps> = ({ 
  letters, employees, onSubmit, onCancel 
}) => {
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [previewContent, setPreviewContent] = useState('');
  const [isPreviewEditable, setIsPreviewEditable] = useState(false);
  const [editableContent, setEditableContent] = useState('');

  const {
    register, handleSubmit,
    formState: { errors, isSubmitting },
    watch
  } = useForm<Partial<LetterGeneration>>({
    defaultValues: { letterId: '', customContent: '', recipients: [''], notes: '', branchId: '' }
  });

  const selectedLetterId = watch('letterId');
  const customContent = watch('customContent');
  const selectedBranchId = watch('branchId');

  const letterOptions = letters.map(letter => ({ value: letter.id, label: letter.templateName }));
  const shipBranches = (apiBranches as any[]).filter((branch: any) => branch.type === 'vessel');
  const branchOptions = shipBranches.map((branch: any) => ({
    value: branch.id?.toString() || branch.documentId || '', label: branch.name
  }));

  const selectedLetter = useMemo(() => letters.find(letter => letter.id === selectedLetterId), [letters, selectedLetterId]);
  const selectedBranch = useMemo(() => (apiBranches as any[]).find((b: any) => 
    b.id?.toString() === selectedBranchId || b.documentId === selectedBranchId
  ), [apiBranches, selectedBranchId]);

  const employeeData = useMemo(() => {
    return selectedEmployees.map(empId => {
      const employee = employees.find(e => e.id === empId);
      if (!employee) return null;
      return { name: employee.name || '', job: employee.position || '', nationalId: employee.nationalId || '', address: employee.address || '' };
    }).filter(Boolean) as { name: string; job: string; nationalId: string; address: string }[];
  }, [selectedEmployees, employees]);

  const branchName = selectedBranch?.name || '............';

  const generateArabicLetterHTML = useMemo(() => {
    if (employeeData.length === 0) return '';
    return generatePersonnelPreviewHTML(employeeData, branchName);
  }, [employeeData, branchName]);

  React.useEffect(() => {
    if (selectedLetter && selectedEmployees.length > 0) {
      if (customContent) {
        let content = customContent;
        content = content.replace('{{EMPLOYEE_DETAILS}}', generateArabicLetterHTML);
        content = content.replace('{{DATE}}', new Date().toLocaleDateString('ar-EG'));
        content = content.replace('{{COMPANY_NAME}}', 'شركة بترومارين لدعم المشاريع البحرية');
        setPreviewContent(content);
      } else {
        setPreviewContent(generateArabicLetterHTML);
      }
      if (!isPreviewEditable) setEditableContent(customContent || generateArabicLetterHTML);
    } else {
      setPreviewContent('');
      setEditableContent('');
    }
  }, [selectedLetter, selectedEmployees, generateArabicLetterHTML, customContent, isPreviewEditable]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(buildPersonnelPrintHTML(employeeData, branchName));
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev => prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]);
  };

  const handleSelectAllEmployees = () => {
    setSelectedEmployees(prev => prev.length === employees.length ? [] : employees.map(e => e.id));
  };

  const onSubmitForm = (data: Partial<LetterGeneration>) => {
    onSubmit({ ...data, selectedItems: selectedEmployees, recipients: data.recipients?.filter(email => email.trim() !== '') || [] });
  };

  const emptyIcon = (
    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">Generate Personnel Letter</h2>
        </div>
        <form onSubmit={handleSubmit(onSubmitForm)} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Select label="Letter Template" {...register('letterId', { required: 'Please select a letter template' })}
                options={letterOptions} error={errors.letterId?.message} placeholder="Select letter template..." />
              <Select label="الوحدة (Unit/Branch)" {...register('branchId', { required: 'Please select a branch' })}
                options={branchOptions} error={errors.branchId?.message} placeholder="Select unit/branch..." />
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Select Employees ({selectedEmployees.length} selected)</label>
                  <button type="button" onClick={handleSelectAllEmployees} className="text-sm text-primary-600 hover:text-primary-700">
                    {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
                  {employees.map(employee => (
                    <label key={employee.id} className="flex items-center space-x-2">
                      <input type="checkbox" checked={selectedEmployees.includes(employee.id)} onChange={() => handleEmployeeToggle(employee.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                      <span className="text-sm text-gray-900">{employee.code} - {employee.name} ({employee.position})</span>
                    </label>
                  ))}
                </div>
                {selectedEmployees.length === 0 && <p className="text-sm text-red-600 mt-1">Please select at least one employee</p>}
              </div>
              <div>
                <TextArea label="Custom Content (Optional)" {...register('customContent')}
                  placeholder="Leave empty to use Arabic template, or customize the letter content here..." rows={6} />
                <p className="text-sm text-gray-500 mt-1">If provided, this will override the template content</p>
              </div>
              <Input label="Recipients (Email)" {...register('recipients.0')} placeholder="Enter recipient email address" type="email" />
              <TextArea label="Notes (Optional)" {...register('notes')} placeholder="Additional notes about this letter generation..." rows={3} />
            </div>
            <LetterPreviewPanel
              showPreview={!!selectedLetter && selectedEmployees.length > 0}
              isEditable={isPreviewEditable}
              previewContent={previewContent}
              editableContent={editableContent}
              onEditableChange={setEditableContent}
              onEdit={() => { setIsPreviewEditable(true); setEditableContent(previewContent); }}
              onSave={() => { setIsPreviewEditable(false); setPreviewContent(editableContent); }}
              onCancel={() => { setIsPreviewEditable(false); setEditableContent(previewContent); }}
              onPrint={handlePrint}
              emptyIcon={emptyIcon}
              emptyMessage="Select a template and employees to preview the letter"
            />
          </div>
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-8">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || selectedEmployees.length === 0 || !selectedLetterId} loading={isSubmitting}>
              Generate Letter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonnelLetterGenerator;
